import {
  anchorModeFor,
  sanitizeAnchorTour,
  type MmMatchMsg,
  type MmMode,
  type MmServerMsg,
} from '@/net/matchmaking';

/**
 * MatchmakingClient.ts — the tiny client side of a Quick Match / Start a Tour
 * search (M44). Opens one WebSocket to the matchmaker (`?mm=quick|tour`),
 * surfaces the live "racers found" count, and hands over the match config when
 * it lands (Flow then connects to the referee room like any other online race).
 * Cancelling just closes the socket — the matchmaker drops us from the batch.
 */
export interface MatchmakingCallbacks {
  onQueue: (found: number, total: number) => void;
  onMatch: (match: MmMatchMsg) => void;
  /** The search failed: server unreachable, or the socket dropped pre-match. */
  onError: () => void;
}

export class MatchmakingClient {
  private readonly ws: WebSocket;
  private settled = false; // matched or cancelled — no error callbacks after

  constructor(baseWsUrl: string, mode: MmMode, private readonly cb: MatchmakingCallbacks) {
    this.ws = new WebSocket(`${baseWsUrl}?mm=${mode}`);
    this.ws.addEventListener('message', (e) => this.onMessage(e));
    // The matchmaker closes the socket itself right after a match — only a
    // close BEFORE that (or a connect failure) is an error.
    this.ws.addEventListener('error', () => this.fail());
    this.ws.addEventListener('close', () => this.fail());
  }

  private onMessage(e: MessageEvent): void {
    let m: MmServerMsg;
    try {
      m = JSON.parse(typeof e.data === 'string' ? e.data : '') as MmServerMsg;
    } catch {
      return;
    }
    if (m.t === 'queue') {
      this.cb.onQueue(m.found, m.total);
    } else if (m.t === 'match') {
      this.settled = true;
      this.cb.onMatch(m);
    }
  }

  private fail(): void {
    if (this.settled) return;
    this.settled = true;
    this.cb.onError();
  }

  /** Leave the queue (also used to tidy up after a match/error). */
  cancel(): void {
    this.settled = true;
    try {
      this.ws.close();
    } catch {
      /* already closed */
    }
  }
}

/**
 * M140 — the PUBLIC crew room's "anchor": while a crew searches (FIND RACERS),
 * the member who pressed it holds one socket to the matchmaker that offers
 * the room's empty seats to searchers in this region. The matchmaker accepts
 * it only for a genuine open public crew race (the race uuid is checked
 * against the database, M140b). It lives a little less than the room's own
 * search window, so a routed stranger always arrives before the seats close,
 * and it is told whenever the open seats change. Best effort: if it can't
 * connect, the room simply stages AI into the seats.
 *
 * M142: a club TOUR (tour 3-9) anchors on the Start a Tour matchmaker and
 * takes those searchers at any length; a single race (tour 0) anchors on
 * Quick Match as before. The mode MUST match the race the database knows,
 * or the matchmaker refuses the anchor.
 */
export class CrewAnchorClient {
  private ws: WebSocket | null = null;
  private lastSeats: number;

  constructor(
    baseWsUrl: string,
    spec: {
      race: string;
      room: string;
      h: string;
      seats: number;
      field: number;
      city: string;
      /** M142: 0 = a single race (Quick Match); 3-9 = a club tour (Start a Tour). */
      tour: number;
      ttlMs: number;
    },
    onRouted?: (seatsLeft: number) => void,
  ) {
    this.lastSeats = spec.seats;
    if (spec.seats < 1) return; // a full crew needs no strangers
    const tour = sanitizeAnchorTour(spec.tour);
    // h / field / city / tour ride along for LOCAL testing only (a production
    // matchmaker takes them from the database, never from here). The MODE is
    // real: it picks which searchers this room is offered to.
    const q =
      `?mm=${anchorModeFor(tour)}&anchor=${encodeURIComponent(spec.room)}` +
      `&race=${encodeURIComponent(spec.race)}` +
      `&seats=${spec.seats}&ttl=${Math.round(spec.ttlMs)}` +
      `&h=${encodeURIComponent(spec.h)}&field=${spec.field}&city=${encodeURIComponent(spec.city)}` +
      `&tour=${tour}`;
    try {
      this.ws = new WebSocket(`${baseWsUrl}${q}`);
      this.ws.addEventListener('message', (e) => {
        try {
          const m = JSON.parse(typeof e.data === 'string' ? e.data : '') as {
            t?: string;
            seats?: number;
          };
          if (m.t === 'routed' && typeof m.seats === 'number') onRouted?.(m.seats);
        } catch {
          /* ignore */
        }
      });
    } catch {
      this.ws = null;
    }
  }

  /** The room's open seats changed: offer exactly that many (0 = stop). */
  setSeats(seats: number): void {
    if (seats === this.lastSeats) return;
    this.lastSeats = seats;
    try {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ t: 'seats', n: seats }));
      }
    } catch {
      /* the anchor is best effort */
    }
  }

  close(): void {
    try {
      this.ws?.close();
    } catch {
      /* already closed */
    }
    this.ws = null;
  }
}
